interface BackendTokenResponse {
  token: string
  expiresAt: number
}

/**
 * Client for interacting with the Playbooks Backend Service API.
 * Handles token management and makes direct calls to the Playbooks Backend Service.
 * See `auth.md` in Playbooks Backend Service docs for details.
 */
export class PlaybooksBackendClient {
  private backendUrl?: string
  private tokenIssuerEndpoint?: string
  private tokenIssuerApiToken?: string

  constructor({
    playbooksBackendUrl,
    tokenIssuerEndpoint,
    tokenIssuerApiToken,
  }: {
    /** Playbooks Backend Service base URL */
    playbooksBackendUrl?: string
    /** URL of the API endpoint to fetch Playbooks Backend Service tokens from */
    tokenIssuerEndpoint?: string
    /** Internal API token to authenticate backend service token requests */
    tokenIssuerApiToken?: string
  }) {
    this.backendUrl = playbooksBackendUrl
    this.tokenIssuerEndpoint = tokenIssuerEndpoint
    this.tokenIssuerApiToken = tokenIssuerApiToken
  }

  /**
   * Generate text using LLM based on the provided prompt
   * @param params Text generation parameters
   * @returns Promise that resolves to the generated text
   */
  public async generateText(params: GenerateTextParams): Promise<string> {
    const result = await this.fetchJson<{ text: string }>('ai/text', {
      method: 'POST',
      body: JSON.stringify({
        prompt: params.prompt,
        model: params.model,
        'system-prompt': params.systemPrompt,
        'reasoning-effort': params.reasoningEffort,
      }),
    })

    return result.text
  }

  /**
   * Generate text using LLM and stream text chunks as they arrive.
   * @param params Text generation parameters
   * @param onChunk Callback invoked for each generated text chunk
   * @returns Promise that resolves to the complete generated text
   */
  public async generateTextStream(params: GenerateTextParams, onChunk: (chunk: string) => void): Promise<string> {
    const response = await this.fetch('ai/text', {
      method: 'POST',
      body: JSON.stringify({
        prompt: params.prompt,
        model: params.model,
        'system-prompt': params.systemPrompt,
        'reasoning-effort': params.reasoningEffort,
        stream: true,
      }),
    })

    if (!response.body) {
      throw new Error('Streaming response body is not available')
    }

    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let generatedText = ''

    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      const chunk = decoder.decode(value, { stream: true })
      generatedText += chunk
      onChunk(chunk)
    }

    const finalChunk = decoder.decode()
    if (finalChunk) {
      generatedText += finalChunk
      onChunk(finalChunk)
    }

    return generatedText
  }

  /**
   * Convert text to speech and get a streaming URL
   * @param params Speech generation parameters
   * @returns Promise that resolves to a URL that can be used as src in an audio element
   */
  public async generateSpeech(params: SpeechGenerationParams): Promise<string> {
    const result = await this.fetchJson<{ requestId: string }>('ai/speech', {
      method: 'POST',
      body: JSON.stringify(params),
    })

    return `${this.backendUrl}/v1/ai/speech?requestId=${result.requestId}`
  }

  /**
   * Generate icons for assessment responses using AI
   * @param question The assessment question
   * @param responses Array of possible responses
   * @returns Promise that resolves to an array of icon names
   */
  public async generateAssessmentIcons(question: string, responses: string[]): Promise<string[]> {
    const result = await this.fetchJson<{ icons: string[] }>('ai/playbooks/assessment-icons', {
      method: 'POST',
      body: JSON.stringify({ question, responses }),
    })

    return result.icons
  }

  /**
   * Generate responses for a play using LLM
   * @param params Play response generation parameters
   * @returns Promise that resolves to the generated responses
   */
  public async generatePlayResponses(params: GeneratePlayResponsesParams): Promise<GeneratedPlayResponse[]> {
    return this.fetchJson<GeneratedPlayResponse[]>('ai/plays/responses', {
      method: 'POST',
      body: JSON.stringify(params),
    })
  }

  /**
   * Initiate a realtime AI session for the Teammate Widget,
   * allowing to send user's audio and stream back AI responses in real time.
   * This endpoint receives an SDP offer from the client, creates a WebRTC session for OpenAI Realtime API
   * using the Unified Interface, and returns an SDP answer from OpenAI.
   * @returns SDP answer to establish the WebRTC connection with OpenAI Realtime API
   */
  public async createTeammateSession(params: TeammateSessionParams): Promise<{ sdpAnswer: string }> {
    return this.fetchJson<{ sdpAnswer: string }>('ai/plays/teammate/session', {
      method: 'POST',
      body: JSON.stringify(params),
    })
  }

  /**
   * Fetch a fresh token from the API
   */
  private async getToken(): Promise<string> {
    if (!this.tokenIssuerEndpoint) {
      throw new Error('Token issuer endpoint is not configured')
    }

    const response = await fetch(this.tokenIssuerEndpoint, {
      method: 'POST',
      headers: {
        ...(this.tokenIssuerApiToken ? { Authorization: `Bearer ${this.tokenIssuerApiToken}` } : {}),
      },
    })

    if (!response.ok) {
      throw new Error(`Failed to fetch Playbooks token: ${response.statusText}`)
    }

    const data: BackendTokenResponse = await response.json()
    return data.token
  }

  /**
   * Make an authenticated request to the Playbooks Backend Service,
   * assuming JSON response type.
   * @param path API endpoint path (without /v1/ prefix)
   * @param options Fetch options (method, body, headers, etc.)
   * @returns Promise that resolves to the response data
   */
  private async fetchJson<T>(path: string, options: RequestInit = {}): Promise<T> {
    const response = await this.fetch(path, options)

    return response.json()
  }

  /**
   * Make an authenticated request to the Playbooks Backend Service
   * @param path API endpoint path (without /v1/ prefix)
   * @param options Fetch options (method, body, headers, etc.)
   * @returns Promise that resolves to the response data
   */
  private async fetch(path: string, options: RequestInit = {}): Promise<Response> {
    const token = await this.getToken()

    if (!this.backendUrl) {
      throw new Error('Playbooks Backend Service base URL is not configured')
    }

    const { headers, ...restOptions } = options
    const response = await fetch(`${this.backendUrl}/v1/${path}`, {
      ...restOptions,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...headers,
      },
    })

    if (!response.ok) {
      const errorText = await response.text()
      console.error('Playbooks Backend Service request failed:', {
        status: response.status,
        path,
        body: errorText,
      })
      throw new Error(errorText || response.statusText || 'API request failed')
    }

    return response
  }
}

interface GenerateTextParams {
  /** The user prompt to guide the AI's response */
  prompt: string
  /** Optional AI model to use */
  model?: string
  /** Optional system prompt */
  systemPrompt?: string
  /** Optional reasoning effort level */
  reasoningEffort?: string
}
interface GeneratePlayResponsesParams {
  /** The empty part of the Action card that the AI needs to fill out */
  actionCard: string
  /** Optional user prompt to guide the AI's response */
  prompt?: string
  /** Optional knowledge context for the AI */
  knowledge?: string
  /** Optional AI model to use */
  model?: string
  /** Optional reasoning effort level */
  reasoningEffort?: string
}

export interface GeneratedPlayResponse {
  questionId: number
  answerIds?: any[]
  answer?: any
  confidence: number
  label?: string // Used in AiTeammateWidget for UI feedback
  timestamp?: number // Used in AiTeammateWidget for UI feedback
}

interface SpeechGenerationParams {
  /** The text to convert to speech */
  text: string
  /** Optional voice (alloy, echo, fable, onyx, nova, shimmer) */
  voice?: 'alloy' | 'echo' | 'fable' | 'onyx' | 'nova' | 'shimmer'
  /** Optional model (tts-1 or tts-1-hd) */
  model?: 'tts-1' | 'tts-1-hd'
}

interface TeammateSessionParams {
  /** The SDP offer from the client */
  sdp: string
  /** Serialized Action Card sections to provide context for the AI teammate */
  actionCardSections: string[]
  /** Client-local current date for resolving relative dates like "today" in the speech input */
  currentDate?: string
  /** Whether OpenAI should transcribe the input audio for transcript recording */
  recordTranscript?: boolean
  /** Whether OpenAI should call tools to fill out the playbook automatically */
  fillAutomatically?: boolean
}
